import React from 'react';
import styled from 'styled-components';
import { Toast, ToastProps } from './Toast';
import {
  toastHistoryStore,
  getStackTrace,
  type ToastHistoryEntry,
} from './ToastHistory';
import { ToastLogPanel } from './ToastLogPanel';
import { UseAuth } from '@/contexts/AuthContext';
import { DEBUG_ALLOWED_USER_IDS } from '../../../contexts/DebugContext';

// ============= Types =============
export type ToastData = Omit<ToastProps, 'onClose'>;
type ToastKind = NonNullable<ToastProps['type']>;
const DEFAULT_ERROR_TOAST_DURATION_MS = 7000;

const normalizeToastType = (type?: ToastProps['type']): ToastKind =>
  type ?? 'info';

// ============= Styled Components =============
const Container = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

// ============= Context =============
interface ToastContextType {
  showToast: (toast: Omit<ToastData, 'id'>) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
}

export const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined,
);

// ============= Provider =============
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);
  const [history, setHistory] = React.useState(toastHistoryStore.getHistory());
  const { currentUser } = UseAuth();
  const currentUserId = currentUser?.userId ?? null;
  const canSeeLogs =
    currentUserId != null && DEBUG_ALLOWED_USER_IDS.includes(currentUserId);
  const filteredHistory = React.useMemo(
    () =>
      canSeeLogs
        ? history
        : history.filter((entry) => (entry.entryKind ?? 'toast') !== 'log'),
    [canSeeLogs, history],
  );

  // Restore persisted log history for debug users on mount
  React.useEffect(() => {
    if (!canSeeLogs || typeof sessionStorage === 'undefined') return;
    try {
      const key = toastHistoryStore.getStorageKey();
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as ToastHistoryEntry[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          toastHistoryStore.loadFromStorage(parsed);
          setHistory(toastHistoryStore.getHistory());
        }
      }
    } catch {
      // ignore invalid or missing data
    }
  }, [canSeeLogs]);

  // Subscribe to history changes; persist for debug users
  React.useEffect(() => {
    const unsubscribe = toastHistoryStore.subscribe(() => {
      const next = toastHistoryStore.getHistory();
      setHistory(next);
      if (canSeeLogs && typeof sessionStorage !== 'undefined') {
        try {
          const serialized = toastHistoryStore.serializeForStorage();
          if (serialized != null) {
            sessionStorage.setItem(
              toastHistoryStore.getStorageKey(),
              serialized,
            );
          }
        } catch {
          // ignore quota or other errors
        }
      }
    });
    return unsubscribe;
  }, [canSeeLogs]);

  const showToast = React.useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const toastData = { ...toast, id };
    const toastType = normalizeToastType(toast.type);

    // Get stack trace to see where toast was called from
    const source = getStackTrace();

    // Log toast for debugging
    console.log('🍞 Toast shown:', {
      id,
      type: toastType,
      message: toast.message,
      duration: toast.duration || 5000,
      timestamp: new Date().toISOString(),
      source,
    });

    // Add to history as user-facing toast (humanized message we show to users)
    toastHistoryStore.addEntry({
      entryKind: 'toast',
      type: toastType,
      message: toast.message,
      duration: toast.duration,
      source,
    });

    setToasts((prev) => [...prev, toastData]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => {
      const toast = prev.find((t) => t.id === id);
      if (toast) {
        console.log('🍞 Toast closed:', {
          id,
          type: toast.type || 'info',
          message: toast.message,
          timestamp: new Date().toISOString(),
        });

        // Mark as closed in history
        toastHistoryStore.markClosed(id);
      }
      return prev.filter((toast) => toast.id !== id);
    });
  }, []);

  const clearHistory = React.useCallback(() => {
    toastHistoryStore.clear();
  }, []);

  const showSuccess = React.useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'success', duration });
    },
    [showToast],
  );

  const showError = React.useCallback(
    (message: string, duration?: number) => {
      showToast({
        message,
        type: 'error',
        duration: duration ?? DEFAULT_ERROR_TOAST_DURATION_MS,
      });
    },
    [showToast],
  );

  const showInfo = React.useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'info', duration });
    },
    [showToast],
  );

  const showWarning = React.useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'warning', duration });
    },
    [showToast],
  );

  const value = React.useMemo(
    () => ({
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
    }),
    [showToast, showSuccess, showError, showInfo, showWarning],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Container>
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={removeToast} />
        ))}
      </Container>
      <ToastLogPanel
        history={filteredHistory}
        onClear={clearHistory}
        isDebugUser={canSeeLogs}
      />
    </ToastContext.Provider>
  );
};

// ============= Hook =============
export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
